// src/components/CategorySelector/CategorySelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Search, ArrowLeft, Check, Plus, X } from 'lucide-react';
import { getChildCategories, getProductCategory, createProductCategory } from '../../../services/api/categories';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './CategorySelector.module.css';

const CategorySelector = ({ 
  selectedCategoryId, 
  onCategorySelect, 
  placeholder = "Choose a product category",
  error = null,
  allowCreateNew = false
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryPath, setSelectedCategoryPath] = useState([]);
  const [categoryCache, setCategoryCache] = useState(new Map());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const createInputRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewCategoryName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load root categories when component mounts or dropdown opens
  useEffect(() => {
    if (isOpen && categories.length === 0 && breadcrumb.length === 0) {
      loadCategories();
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && !showCreateForm) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, showCreateForm]);

  // Focus create input when create form opens
  useEffect(() => {
    if (showCreateForm && createInputRef.current) {
      setTimeout(() => {
        createInputRef.current.focus();
      }, 100);
    }
  }, [showCreateForm]);

  // Initialize selectedCategory if we have a selectedCategoryId but no selectedCategory
  useEffect(() => {
    if (selectedCategoryId && (!selectedCategory || selectedCategory.id !== selectedCategoryId)) {
      loadSelectedCategoryDetails();
    }
  }, [selectedCategoryId]);

  // Separate effect to handle clearing when no ID is provided
  useEffect(() => {
    if (!selectedCategoryId && selectedCategory) {
      setSelectedCategory(null);
      setSelectedCategoryPath([]);
    }
  }, [selectedCategoryId, selectedCategory]);

  // When dropdown opens and there's a selected category, restore the path
  useEffect(() => {
    if (isOpen && selectedCategoryPath.length > 0 && categories.length === 0) {
      restoreSelectedCategoryPath();
    }
  }, [isOpen, selectedCategoryPath]);

  const loadSelectedCategoryDetails = async () => {
    try {
      if (categoryCache.has(selectedCategoryId)) {
        const cachedCategory = categoryCache.get(selectedCategoryId);
        setSelectedCategory(cachedCategory);
        return;
      }

      const categoryResponse = await getProductCategory(selectedCategoryId);
      
      if (categoryResponse && (categoryResponse.category || categoryResponse.id)) {
        const category = categoryResponse.category || categoryResponse;
        
        setCategoryCache(prev => new Map(prev.set(selectedCategoryId, category)));
        setSelectedCategory(category);
        await buildCategoryPath(category);
      }
    } catch (error) {
      console.error('Error loading selected category:', error);
      setSelectedCategory({ 
        id: selectedCategoryId, 
        name: `Category ${selectedCategoryId}` 
      });
    }
  };

  const buildCategoryPath = async (category) => {
    try {
      const path = [];
      let currentCategory = category;
      
      while (currentCategory && currentCategory.parentId) {
        try {
          let parentCategory;
          
          if (categoryCache.has(currentCategory.parentId)) {
            parentCategory = categoryCache.get(currentCategory.parentId);
          } else {
            const parentResponse = await getProductCategory(currentCategory.parentId);
            if (parentResponse) {
              parentCategory = parentResponse.category || parentResponse;
              setCategoryCache(prev => new Map(prev.set(currentCategory.parentId, parentCategory)));
            }
          }
          
          if (parentCategory && parentCategory.id) {
            path.unshift({
              id: parentCategory.id,
              name: parentCategory.name
            });
            currentCategory = parentCategory;
          } else {
            break;
          }
        } catch (error) {
          console.error('Error loading parent category:', error);
          break;
        }
      }
      
      setSelectedCategoryPath(path);
    } catch (error) {
      console.error('Error building category path:', error);
    }
  };

  const restoreSelectedCategoryPath = async () => {
    try {
      const lastPathItem = selectedCategoryPath[selectedCategoryPath.length - 1];
      if (lastPathItem) {
        setBreadcrumb([...selectedCategoryPath]);
        await loadCategories(lastPathItem.id);
      }
    } catch (error) {
      console.error('Error restoring category path:', error);
      loadCategories();
    }
  };

  const loadCategories = async (parentId = null) => {
    try {
      setLoading(true);
      const response = await getChildCategories(parentId);
      setCategories(response || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = async (category) => {
    try {
      const children = await getChildCategories(category.id);
      if (children && children.length > 0) {
        // Has children - navigate into category
        const newBreadcrumb = [...breadcrumb, { id: category.id, name: category.name }];
        setBreadcrumb(newBreadcrumb);
        setCategories(children);
        setSearchTerm('');
      } else {
        // No children - just select the category
        handleCategorySelect(category);
      }
    } catch (error) {
      // On error, just select the category
      handleCategorySelect(category);
    }
  };

  const handleCategoryBrowse = async (category) => {
    try {
      // Navigate into category regardless of whether it has children
      const newBreadcrumb = [...breadcrumb, { id: category.id, name: category.name }];
      setBreadcrumb(newBreadcrumb);
      
      // Load children (might be empty)
      const children = await getChildCategories(category.id);
      setCategories(children || []);
      setSearchTerm('');
    } catch (error) {
      console.error('Error browsing category:', error);
      // Still navigate, but with empty categories
      const newBreadcrumb = [...breadcrumb, { id: category.id, name: category.name }];
      setBreadcrumb(newBreadcrumb);
      setCategories([]);
      setSearchTerm('');
    }
  };

  const handleCategorySelect = (category) => {
    setCategoryCache(prev => new Map(prev.set(category.id, category)));
    setSelectedCategory(category);
    
    const categoryPath = [...breadcrumb, { id: category.id, name: category.name }];
    setSelectedCategoryPath(categoryPath.slice(0, -1));
    
    onCategorySelect(category.id);
    setIsOpen(false);
    
    setBreadcrumb([]);
    setSearchTerm('');
    setCategories([]);
  };

  const handleBreadcrumbClick = async (index) => {
    if (index === -1) {
      setBreadcrumb([]);
      loadCategories();
    } else {
      const targetCategory = breadcrumb[index];
      setBreadcrumb(prev => prev.slice(0, index + 1));
      loadCategories(targetCategory.id);
    }
    setSearchTerm('');
    setShowCreateForm(false);
  };

  const handleBackClick = () => {
    if (breadcrumb.length === 0) {
      return;
    }
    
    if (breadcrumb.length === 1) {
      setBreadcrumb([]);
      loadCategories();
    } else {
      const parentCategory = breadcrumb[breadcrumb.length - 2];
      setBreadcrumb(prev => prev.slice(0, -1));
      loadCategories(parentCategory.id);
    }
    setSearchTerm('');
    setShowCreateForm(false);
  };

  const handleSelectorClick = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateNewClick = () => {
    setShowCreateForm(true);
    setSearchTerm('');
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewCategoryName('');
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      showError(t('categories.nameRequired'));
      return;
    }

    try {
      setIsCreating(true);
      
      const parentId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : null;
      
      const categoryData = {
        name: newCategoryName.trim(),
        parentId: parentId
      };

      const response = await createProductCategory(categoryData);
      const newCategory = response.category || response;
      
      // Add to cache
      setCategoryCache(prev => new Map(prev.set(newCategory.id, newCategory)));
      
      // Refresh categories list
      await loadCategories(parentId);
      
      // Auto-select the new category
      handleCategorySelect(newCategory);
      
      success(t('categories.categoryCreatedSuccess', { categoryName: newCategory.name }));
      
      setShowCreateForm(false);
      setNewCategoryName('');
      
    } catch (error) {
      console.error('Error creating category:', error);
      showError(error.response?.data?.message || t('categories.failedToCreate'));
    } finally {
      setIsCreating(false);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.categorySelector} ref={dropdownRef}>
      <div 
        className={`${styles.selectorInput} ${error ? styles.error : ''} ${isOpen ? styles.open : ''}`}
        onClick={handleSelectorClick}
      >
        <span className={styles.selectedText}>
          {selectedCategory ? selectedCategory.name : placeholder}
        </span>
        <ChevronDown 
          size={18} 
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
        />
      </div>

      {error && <span className={styles.errorText}>{error}</span>}

      {isOpen && (
        <div className={styles.dropdown}>
          {/* Search Input or Create Form */}
          {!showCreateForm ? (
            <div className={styles.searchContainer}>
              <Search size={16} className={styles.searchIcon} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('categories.searchCategories')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          ) : (
            <div className={styles.createContainer}>
              <div className={styles.createForm}>
                <div className={styles.createInputGroup}>
                  <input
                    ref={createInputRef}
                    type="text"
                    placeholder={t('categories.categoryNamePlaceholder')}
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateCategory();
                      }
                      if (e.key === 'Escape') {
                        handleCancelCreate();
                      }
                    }}
                    className={styles.createInput}
                    disabled={isCreating}
                  />
                  <div className={styles.createActions}>
                    <button
                      type="button"
                      className={styles.createButton}
                      onClick={handleCreateCategory}
                      disabled={isCreating || !newCategoryName.trim()}
                    >
                      {isCreating ? (
                        <div className={styles.createSpinner}></div>
                      ) : (
                        <Check size={14} />
                      )}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={handleCancelCreate}
                      disabled={isCreating}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
                {breadcrumb.length > 0 && (
                  <div className={styles.createHint}>
                    {t('categories.createUnder')}: {breadcrumb[breadcrumb.length - 1].name}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Breadcrumb */}
          {breadcrumb.length > 0 && (
            <div className={styles.breadcrumb}>
              <button
                onClick={handleBackClick}
                className={styles.backButton}
              >
                <ArrowLeft size={14} />
              </button>
              <div className={styles.breadcrumbPath}>
                <button
                  onClick={() => handleBreadcrumbClick(-1)}
                  className={styles.breadcrumbItem}
                >
                  {t('categories.allCategories')}
                </button>
                {breadcrumb.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ChevronRight size={12} className={styles.breadcrumbSeparator} />
                    <button
                      onClick={() => handleBreadcrumbClick(index)}
                      className={styles.breadcrumbItem}
                    >
                      {item.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Categories List */}
          <div className={styles.categoriesList}>
            {loading ? (
              <div className={styles.loadingState}>
                <div className={styles.loadingSpinner}></div>
                <span>{t('categories.loadingCategories')}</span>
              </div>
            ) : (
              <>
                {/* Add New Category Button */}
                {allowCreateNew && !showCreateForm && (
                  <button
                    onClick={handleCreateNewClick}
                    className={styles.addNewButton}
                  >
                    <Plus size={16} className={styles.addNewIcon} />
                    <span>{t('categories.addCategory')}</span>
                  </button>
                )}

                {/* Categories */}
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => (
                    <CategoryItem
                      key={category.id}
                      category={category}
                      onClick={() => handleCategoryClick(category)}
                      onSelect={() => handleCategorySelect(category)}
                      onBrowse={() => handleCategoryBrowse(category)}
                      isSelected={selectedCategoryId === category.id}
                      allowCreateNew={allowCreateNew}
                    />
                  ))
                ) : (
                  !showCreateForm && (
                    <div className={styles.emptyState}>
                      {searchTerm ? t('categories.noSearchResults') : t('categories.noCategories')}
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CategoryItem = ({ category, onClick, onSelect, onBrowse, isSelected, allowCreateNew }) => {
  const [hasChildren, setHasChildren] = useState(null);

  useEffect(() => {
    const checkChildren = async () => {
      try {
        const children = await getChildCategories(category.id);
        setHasChildren(children && children.length > 0);
      } catch (error) {
        setHasChildren(false);
      }
    };
    
    checkChildren();
  }, [category.id]);

  return (
    <div className={`${styles.categoryItem} ${isSelected ? styles.selected : ''}`}>
      <div className={styles.categoryContent} onClick={onClick}>
        <span className={styles.categoryName}>{category.name}</span>
        {hasChildren && (
          <ChevronRight size={16} className={styles.categoryArrow} />
        )}
      </div>
      
      <div className={styles.categoryActions}>
        {/* Browse/Navigate button - only show if allowCreateNew is true or category has children */}
        {(allowCreateNew || hasChildren) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBrowse();
            }}
            className={styles.browseButton}
            title={hasChildren ? "Browse subcategories" : "Create subcategories"}
          >
            <ChevronRight size={12} />
          </button>
        )}
        
        {/* Select button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={`${styles.selectButton} ${!hasChildren && !allowCreateNew ? styles.prominent : ''}`}
          title="Select this category"
        >
          <Check size={14} />
        </button>
      </div>
    </div>
  );
};

export default CategorySelector;